<template>
  <div class="pdp pdp__container container">
    <AppBreadcrumbs />

    <div class="pdp__layout">
      <div class="pdp__product">
        <div class="pdp__product-inner">
          <div
            class="pdp__labels header__labels"
          >
            <div
              v-if="esProduct?.isSale && esProduct?.discount >= 5"
              class="header__labels-inner"
            >
              <ProductLabelSale
                :prices="productPrices"
              />
            </div>
          </div>
          <div class="pdp__header">
            <h1
              v-if="!['GroupedProduct', 'GiftCardProduct'].includes(m2Product?.__typename)"
              class="pdp__heading heading heading--5"
            >
              {{ esProduct?.brand }}
            </h1>
            <h1
              v-if="['GiftCardProduct'].includes(m2Product?.__typename)"
              class="pdp__heading pdp__giftcard-heading heading heading--6"
            >
              {{ esProduct?.name }}
            </h1>
            <span
              v-if="!['GroupedProduct', 'GiftCardProduct'].includes(m2Product?.__typename)"
              class="pdp-header__subtitle subtitle product-name"
              itemprop="name"
            >
              {{ esProduct?.name }} {{ esProduct?.season ? ` - ${esProduct?.season}` : '' }}
            </span>

            <div
              v-if="!['GiftCardProduct'].includes(m2Product?.__typename)"
              class="pdp-header__sku"
            >
              <span>{{ $t('Art.Number: {sku}', { sku: esProduct?.sku || '' }) }}</span>
            </div>
            <ProductGroupedHeading
              v-if="m2Product?.__typename === 'GroupedProduct'"
              :product="m2Product"
            />

            <ProductGiftCardHeading
              v-if="m2Product?.__typename === 'GiftCardProduct'"
              :product="m2Product"
            />
          </div>

          <div
            ref="addToCart"
            class="pdp__info"
          >
            <ProductAddToCart
              class="pdp__addtocart"
              :product="m2Product"
              :outlet-product="esProduct?.outlet"
              @selected="handleSelectedItem"
            >
              <template #below-promise>
                <div
                  v-if="pdpPromotionPeriod?.length"
                  class="message message--warn"
                >
                  <StrapiBlocksText
                    :nodes="pdpPromotionPeriod"
                  />
                </div>

                <ProductRelatedColorProducts
                  v-if="esProduct?.relatedColorProducts?.length"
                  :product="esProduct"
                  class="pdp__color-variants"
                />
              </template>
            </ProductAddToCart>
          </div>
          <AppUsps
            v-if="pdpUsps?.length"
            class="pdp__usps"
            :usps="pdpUsps"
            vertical
          />
          <ProductAvailabilityAvailableLocations
            class="pdp__locations"
            :current-item="selectedItem"
          />
          <div class="pdp__product-info">
            <LazyUiCollapsable
              v-if="!['GiftCardProduct'].includes(m2Product?.__typename) && m2Product?.description?.html"
              class="pdp__description pdp-info__tab"
              :label="$t('Description')"
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span
                class="grouped__product-description-text"
                v-html="m2Product.description.html"
              />
            </LazyUiCollapsable>

            <LazyUiCollapsable
              v-if="sortedAttributeGroups?.length"
              class="pdp__specifications specifications pdp-info__tab"
              :label="$t('Everything about this product')"
            >
              <template
                v-for="(attributeGroup) in sortedAttributeGroups"
                :key="attributeGroup.code"
              >
                <table class="specifications__table table table--spacing table--grid-rows">
                  <tbody>
                    <template
                      v-for="spec in attributeGroup.attributes"
                      :key="spec.code"
                    >
                      <tr v-if="spec.value">
                        <th>{{ spec.label }}</th>
                        <td>{{ spec.value }}</td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </template>

              <ProductGiftCardProductInfo
                v-if="m2Product?.__typename === 'GiftCardProduct'"
                :product="m2Product"
              />
            </LazyUiCollapsable>

            <LazyUiCollapsable
              v-if="pdpDelivery"
              class="pdp__delivery pdp-info__tab"
              :label="pdpDelivery?.CmsTitle || $t('Delivery & collection?')"
            >
              <div
                v-if="pdpDelivery?.Richtexts?.[0]?.Richtext?.length"
                class="pdp__info-richtext"
              >
                <StrapiBlocksText :nodes="pdpDelivery?.Richtexts?.[0]?.Richtext" />
              </div>
            </LazyUiCollapsable>

            <LazyUiCollapsable
              v-if="pdpReturn"
              class="pdp__return pdp-info__tab"
              :label="pdpReturn?.CmsTitle || $t('Exchanges & returns?')"
            >
              <div
                v-if="pdpReturn?.Richtexts?.[0]?.Richtext?.length"
                class="pdp__info-richtext"
              >
                <StrapiBlocksText :nodes="pdpReturn?.Richtexts?.[0]?.Richtext" />
              </div>
            </LazyUiCollapsable>

            <LazyUiCollapsable
              v-if="pdpContact?.Icons?.length"
              class="pdp__contact pdp-info__tab"
              :label="pdpContact?.Title || $t('Still have a question?')"
            >
              <ProductContact
                :title="pdpContact?.Title"
                :description="pdpContact?.Description"
                :icons="pdpContact?.Icons"
              />
            </LazyUiCollapsable>
          </div>
        </div>
      </div>

      <div class="pdp__media">
        <div
          v-if="esProduct?.isSale && esProduct?.discount >= 5"
          class="pdp__labels media__labels"
        >
          <ProductLabelSale
            :prices="productPrices"
          />
        </div>

        <UiMediaGallery
          v-if="productMediaGallery"
          :media="productMediaGallery"
        />
      </div>

      <div class="pdp__extra-data">
        <template
          v-for="lookList in looksProducts"
          :key="lookList.sku"
        >
          <ProductShopTheLookSlider
            :current-product="m2Product.product"
            :look-list="lookList"
          />
        </template>
        <ProductSlider
          v-if="relatedProducts?.length"
          :products="relatedProducts"
          class="pdp__slider pdp__related"
        >
          <div class="pdp-slider__header">
            <h6
              class="pdp-slider__heading heading"
              v-text="$t('Maak je outfit compleet')"
            />
          </div>
        </ProductSlider>

        <LazyCatalogQuickLinks
          v-if="categoryBrandLinks.length"
          :title="$t('Related Quick Links')"
          :links="categoryBrandLinks"
          class="pdp__quick-links"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
const route = useRoute()
const meta = inject('meta', ref())
const { t } = useI18n({ useScope: 'global' })

const { public: config, app } = useRuntimeConfig()

const {
  m2Product,
  esProduct,
  parentCategories,
  categories,
  pdpUsps,
  pdpContact,
  relatedProducts,
  looksProducts,
  fetchProductData,
  pdpPromotionPeriod,
  pdpDelivery,
  pdpReturn,
  // interestingProducts
} = usePDP(meta)

await fetchProductData()

provide('esProduct', esProduct)

const selectedItem = ref<any>({})

const handleSelectedItem = (item: any) => {
  selectedItem.value = item
}

const productMediaGallery = computed(() => {
  if (!m2Product?.value?.media_gallery) return null
  return m2Product.value.media_gallery.toSorted((a: any, b: any) => {
    return a.position - b.position
  }).map((media: any) => {
    if (media.video_content) {
      return {
        alt: media.video_content.video_title || media.label,
        src: media.video_content.video_url,
        provider: media.video_content.video_url.includes('vimeo') ? 'vimeo' : undefined,
      }
    }
    return {
      alt: media.label,
      src: media.url,
      visual: media.kind_of_file ?? 'product',
    }
  })
})

const productPrices = computed(() => {
  return {
    regularPrice: m2Product?.value?.price_range?.minimum_price?.regular_price?.value,
    finalPrice: m2Product?.value?.price_range?.minimum_price?.final_price?.value,
  }
})

const categoryBrandLinks = computed(() => {
  const result: any[] = [
    {
      Name: esProduct.value?.brand?.label,
      Link: stringToSlug(esProduct.value?.brand?.label?.replace('&', 'and') || ''),
    },
  ]
  categories.value.forEach((category: any) => {
    if (category?.name && category?.url) {
      result.push({
        Name: category.name,
        Link: category.url,
      })
      if (esProduct?.value?.brand?.label && category?.level < 3) {
        result.push({
          Name: esProduct.value?.brand?.label + ' ' + category.name,
          Link: category.url + '/' + stringToSlug(esProduct.value?.brand?.label?.replace('&', 'and') || ''),
        })
      }
    }
  })
  return result.filter((currentItem, currentIndex, allItems) => {
    return !allItems.some(
      otherItem =>
        otherItem.Link.length > currentItem.Link.length
        && otherItem.Link.startsWith(currentItem.Link),
    )
  })
})

const { setBreadcrumbs } = useBreadcrumbs()
setBreadcrumbs({
  label: esProduct?.value?.name,
  parents: [...parentCategories.value].sort((a, b) => a.level - b.level),
})

const { pageView, viewItem } = useDataLayer()
onMounted(() => {
  pageView('detail', esProduct?.value?.name)
  viewItem(
    esProduct?.value,
    { skus: m2Product.value?.variants?.map((variant: any) => variant.product.sku) || [esProduct.value?.sku] },
  )
})

const metaTitle = computed(
  () => esProduct?.value?.name + ' ' + esProduct?.value?.sku,
)

const metaDescription = computed(
  () => t(`pdp.metaDescription`, { name: esProduct?.value?.name, sku: esProduct?.value?.sku }),
)

useHead({
  title: metaTitle,
  meta: [
    { hid: 'description', name: 'description', content: metaDescription },
    { hid: 'robots', name: 'robots', content: 'index, follow' },
  ],
})

const { canonical } = useSeo()
canonical(computed(() => route.path))

const productSnippet = () => {
  const { locale, locales } = useI18n({ useScope: 'global' })
  const currency = locales.value.find(lcl => lcl.code === locale.value)?.numberFormats?.currency?.currency || 'EUR'
  useHead({
    script: [
      {
        hid: 'Product',
        type: 'application/ld+json',
        innerHTML: {
          '@context': 'https://schema.org',
          '@type': 'Product',
          'name': `${esProduct.value?.name} ${esProduct.value?.descriptiveName}`,
          'image': esProduct.value?.image,
          'sku': esProduct.value?.sku,
          'brand': {
            '@type': 'Brand',
            'name': esProduct.value?.brand?.label,
          },
          'offers': {
            '@type': 'Offer',
            'url': `${config.baseDomain}${app.baseURL}${esProduct.value?.url}`.replace(/\/+/g, '/').replace(':/', '://'), // Safari compatible version
            'priceCurrency': currency,
            'price': esProduct.value?.finalPrice || 0,
            'itemCondition': 'https://schema.org/NewCondition',
            'availability': `https://schema.org/${m2Product.value?.stock_status === 'OUT_OF_STOCK' ? 'OutOfStock' : 'InStock'}`,
          },
        },
      },
    ],
  })
}

productSnippet()

const sortedAttributeGroups = computed(
  () => m2Product?.value.attributeGroups.toSorted((a: any, b: any) => a.order - b.order)
    .map((attributeGroup: any) => ({
      ...attributeGroup,
      attributes: attributeGroup.attributes.toSorted((a: any, b: any) => a.order - b.order),
    })),
)
</script>

<style>
  @import url("~layers/app/pages/~pdp.css");
</style>
